import { useCallback, useState } from 'react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import buildUrl from 'modules/core/lib/buildUrl'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import app from 'modules/core/store/models/app'

import useBulkUpdateFolderDocument from 'modules/documents/hooks/documents/useBulkUpdateFolderDocument'
import useMoveToTrashBinDocument from 'modules/documents/hooks/documents/useMoveToTrashBinDocument'
import useToggleVisibilityDocument from 'modules/documents/hooks/documents/useToggleVisibilityDocument'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'

import TrashBinModal from 'modules/documents/components/BulkActionsDatatable/TrashBinModal'
import UpdateFolderModal from 'modules/documents/components/BulkActionsDatatable/UpdateFolderModal'
import ResendSignatureReminderModal from 'modules/documents/components/BulkActionsDatatable/ResendSignatureReminderModal'

import type { DocumentNode } from './types'

const ACTION_IDS = {
  TOGGLE_VISIBILITY: 'toggle_visibility',
  MOVE_TO_FOLDER: 'move_to_folder',
  RESEND_SIGNATURE: 'resend_signature',
  DOWNLOAD: 'download',
  MOVE_TO_TRASH: 'move_to_trash',
} as const

type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

export function useItemActions() {
  const session = useCurrentSession()

  const [trashModalDoc, setTrashModalDoc] = useState<DocumentNode | null>(null)
  const [folderModalDoc, setFolderModalDoc] = useState<DocumentNode | null>(null)
  const [resendModalDoc, setResendModalDoc] = useState<DocumentNode | null>(null)

  const { moveDocumentToTrashBin, loading: loadingTrashBin } = useMoveToTrashBinDocument({
    fromDataTable: true,
  })
  const { bulkUpdateFolder, loading: loadingUpdateFolder } = useBulkUpdateFolderDocument({
    fromDataTable: true,
  })
  const { bulkResendRequestSignature, loading: loadingResendSignature } =
    useBulkResendRequestSignature()
  const { toggleVisibilityDocument } = useToggleVisibilityDocument()

  const handleAction = useCallback(
    (actionId: string, document: DocumentNode) => {
      switch (actionId) {
        case ACTION_IDS.TOGGLE_VISIBILITY: {
          const currentOwned = document.employee
            ? session.employee?.id === document.employee.id
            : false

          if (currentOwned) return

          // If visible → make private (no confirmation needed)
          // If private → make visible (simplified: no notification prompt in v1)
          toggleVisibilityDocument({
            id: document.id,
            sendNotifications: false,
          }).then((result) => {
            const handled = handleResult(result, () => {
              app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
            })
            if (handled.success) {
              app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
            }
          })
          break
        }
        case ACTION_IDS.MOVE_TO_FOLDER:
          setFolderModalDoc(document)
          break
        case ACTION_IDS.RESEND_SIGNATURE:
          setResendModalDoc(document)
          break
        case ACTION_IDS.DOWNLOAD:
          if (document.file) window.open(buildUrl(document.file), '_blank', 'noopener,noreferrer')
          break
        case ACTION_IDS.MOVE_TO_TRASH:
          setTrashModalDoc(document)
          break
      }
    },
    [session.employee?.id, toggleVisibilityDocument]
  )

  const getItemActions = useCallback(
    (document: DocumentNode) => {
      const actions: Array<{
        id: ActionId
        label: string
        onClick: () => void
        critical?: boolean
      }> = []

      const canEdit = document._policies?.edit?.can ?? false
      const canDelete = document._policies?.delete?.can ?? false
      const currentOwned = document.employee ? session.employee?.id === document.employee.id : false
      const isPendingSignature =
        document.signatureStatus === 'pending' || document.signatureStatus === 'partially_signed'
      const canResend = isPendingSignature && document.status !== 'to_fill'

      // Visibility toggle
      if (canEdit && !currentOwned) {
        actions.push({
          id: ACTION_IDS.TOGGLE_VISIBILITY,
          label: document.public
            ? i18n.t('documents.employee.bulk-actions.hide')
            : i18n.t('documents.employee.bulk-actions.visible'),
          onClick: () => handleAction(ACTION_IDS.TOGGLE_VISIBILITY, document),
        })
      }

      // Move to folder
      if (canEdit) {
        actions.push({
          id: ACTION_IDS.MOVE_TO_FOLDER,
          label: i18n.t('documents.move'),
          onClick: () => handleAction(ACTION_IDS.MOVE_TO_FOLDER, document),
        })
      }

      // Resend signature reminder
      if (canResend) {
        actions.push({
          id: ACTION_IDS.RESEND_SIGNATURE,
          label: i18n.t('documents.employee.bulk-actions.resend_signature_reminder'),
          onClick: () => handleAction(ACTION_IDS.RESEND_SIGNATURE, document),
        })
      }

      // Download
      actions.push({
        id: ACTION_IDS.DOWNLOAD,
        label: i18n.t('documents.download'),
        onClick: () => handleAction(ACTION_IDS.DOWNLOAD, document),
      })

      // Move to trash
      if (canDelete && !document.folder?.showOnly) {
        actions.push({
          id: ACTION_IDS.MOVE_TO_TRASH,
          label: i18n.t('documents.move_to_trash_bin'),
          onClick: () => handleAction(ACTION_IDS.MOVE_TO_TRASH, document),
          critical: true,
        })
      }

      return actions
    },
    [session.employee?.id, handleAction]
  )

  const onSubmitTrash = useCallback(async () => {
    if (!trashModalDoc) return
    const result = await moveDocumentToTrashBin({ documentIds: [trashModalDoc.id] })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    setTrashModalDoc(null)
  }, [trashModalDoc, moveDocumentToTrashBin])

  const onSubmitFolder = useCallback(
    async (selectedFolderId: string | null) => {
      if (!folderModalDoc) return
      const result = await bulkUpdateFolder({
        documentIds: [folderModalDoc.id],
        folderId: selectedFolderId,
      })
      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('documents.employee.bulk-actions.update-folder.success'),
        })
      } else {
        app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
      }
      setFolderModalDoc(null)
    },
    [folderModalDoc, bulkUpdateFolder]
  )

  const onSubmitResend = useCallback(async () => {
    if (!resendModalDoc) return
    const result = await bulkResendRequestSignature({ documentIds: [resendModalDoc.id] })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.employee.bulk-actions.resend-signature.success', {
          count: resendModalDoc.signees?.length || 1,
        }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
    }
    setResendModalDoc(null)
  }, [resendModalDoc, bulkResendRequestSignature])

  const ItemActionModals = useCallback(
    () => (
      <>
        {trashModalDoc && (
          <TrashBinModal
            useF0
            filename={trashModalDoc.filename}
            onClose={() => setTrashModalDoc(null)}
            onSubmit={onSubmitTrash}
            loading={loadingTrashBin}
            hasSignature={trashModalDoc.signatureStatus !== null}
          />
        )}
        {folderModalDoc && (
          <UpdateFolderModal
            useF0
            documents={[folderModalDoc]}
            onClose={() => setFolderModalDoc(null)}
            onSubmit={onSubmitFolder}
            loading={loadingUpdateFolder}
          />
        )}
        {resendModalDoc && (
          <ResendSignatureReminderModal
            useF0
            totalSelectedDocuments={1}
            onClose={() => setResendModalDoc(null)}
            onSubmit={onSubmitResend}
            loading={loadingResendSignature}
          />
        )}
      </>
    ),
    [
      trashModalDoc,
      folderModalDoc,
      resendModalDoc,
      onSubmitTrash,
      onSubmitFolder,
      onSubmitResend,
      loadingTrashBin,
      loadingUpdateFolder,
      loadingResendSignature,
    ]
  )

  return {
    itemActions: getItemActions,
    ItemActionModals,
  }
}
